import type React from "react";
import { useEffect, useState } from "react";
import "./FlashCard.scss";
import { BsArrowLeft, BsArrowRight } from "react-icons/bs";
import { IoIosSwap } from "react-icons/io";
import { TypeToActivityIcon } from "../../../shared/helpers/activity";
import type { WordEntry } from "./types";

type Props = {
	activity: WordEntry[];
};

const FlashCard: React.FC<Props> = ({ activity }) => {
	const [cards, setCards] = useState<WordEntry[]>([]);
	const [increment, setIncrement] = useState<number>(0);
	const [flip, setFlip] = useState(false);
	const [changeSentence, setChangeSentence] = useState(0);

	const ListeningIcon = TypeToActivityIcon("speaker");

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setCards(activity);
	}, []);

	const card = cards[increment];

	const playSound = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		word: string,
		lang: string,
	) => {
		e.stopPropagation();

		const voices = speechSynthesis.getVoices();
		const selectedVoice = voices.find((voice) => voice.lang === lang);

		const utterance = new SpeechSynthesisUtterance(word);
		utterance.lang = lang;
		if (selectedVoice) {
			utterance.voice = selectedVoice;
		}

		speechSynthesis.speak(utterance);
	};

	const handleChangeCards = (
		e: React.MouseEvent<SVGElement, MouseEvent>,
		type: "left" | "right",
	) => {
		e.stopPropagation();
		if (type === "left" && increment > 0) {
			setIncrement((prev) => prev - 1);
			setFlip(false);
		} else if (type === "right" && increment < cards.length - 1) {
			setIncrement((prev) => prev + 1);
			setFlip(false);
		}
	};

	const changeSentences = (
		e:
			| React.MouseEvent<HTMLElement, MouseEvent>
			| React.KeyboardEvent<HTMLDivElement>,
		index: number,
	) => {
		e.stopPropagation();
		setChangeSentence(index);
	};

	const swapWords = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		e.stopPropagation();
		setCards((prevCards) => {
			const newCards = [...prevCards];
			const currentCard = { ...newCards[increment] };

			[currentCard.source, currentCard.target] = [
				currentCard.target,
				currentCard.source,
			];

			currentCard.translations = currentCard.translations.map(
				(translation) => ({
					sourceSent: translation.targetSent,
					targetSent: translation.sourceSent,
				}),
			);

			newCards[increment] = currentCard;
			return newCards;
		});
		setFlip(false);
	};

	return (
		<div
			className={flip ? "flip-card flipped" : "flip-card"}
			onClick={() => setFlip((prev) => !prev)}
			onKeyUp={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					setFlip((prev) => !prev);
				}
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
				}
			}}
		>
			<div className="flip-card-inner">
				<div className="flip-card-front">
					<div className="buttons-holder">
						<button
							className="sound"
							type="button"
							onClick={(e) =>
								playSound(e, card?.source?.word, card?.source?.lang)
							}
						>
							<ListeningIcon />
						</button>
						<button
							type="button"
							className="sound"
							onClick={(e) => swapWords(e)}
						>
							<IoIosSwap />
						</button>
					</div>
					<div className="word-sentences-block">
						<span className="word">{card?.source?.word}</span>
						<span className="sentences">
							{card?.translations[changeSentence]?.sourceSent}
						</span>
						<div className="dots-holder">
							{card?.translations.map((translation, index) => {
								return (
									<div
										onClick={(e) => changeSentences(e, index)}
										onKeyUp={(e) => {
											if (e.key === "Enter" || e.key === " ") {
												changeSentences(e, index);
											}
										}}
										key={translation.sourceSent + translation.targetSent}
										className={index === changeSentence ? "chosen dot" : "dot"}
									/>
								);
							})}
						</div>
					</div>
					{card?.image && (
						<img
							src={card?.image}
							alt="Avatar"
							style={{ width: "300px", height: "300px" }}
						/>
					)}
				</div>
				<div className="flip-card-back">
					<div className="buttons-holder">
						<button
							type="button"
							className="sound"
							onClick={(e) =>
								playSound(e, card?.target?.word, card?.target?.lang)
							}
						>
							<ListeningIcon />
						</button>
					</div>
					<div className="word-sentences-block">
						<span className="word">{card?.target?.word}</span>
						<span className="sentences">
							{card?.translations[changeSentence]?.targetSent}
						</span>
					</div>
				</div>
			</div>
			<div className="bottom-part">
				<button type="button">
					<BsArrowLeft onClick={(e) => handleChangeCards(e, "left")} />
				</button>
				<button type="button">
					<BsArrowRight onClick={(e) => handleChangeCards(e, "right")} />
				</button>
			</div>
		</div>
	);
};

export default FlashCard;
